"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { isValidHttpUrl } from "@/lib/mcp-auth-derive";
import {
  MCPProbeError,
  probeMCP,
  type MCPProbeResult,
} from "@/services/mcp-service";

// A value that is still one of these schemes being typed ("h" … "https://") is
// not a malformed URL yet.
const URL_SCHEMES = ["http://", "https://"];
const CHECK_DELAY_MS = 600;

interface UseMcpUrlCheckOptions {
  /** The Server URL as typed. */
  url: string;
  setUrl: (url: string) => void;
  /** Whether this URL needs a check (the Edit panel skips the stored one). */
  active: boolean;
  probeEnabled?: boolean;
  /** Called after the current URL probed successfully. */
  onProbed?: (url: string) => void;
}

/**
 * Server URL check shared by the Add MCP modal and the Edit MCP panel.
 *
 * Once typing pauses, a malformed URL gets the format error and a valid one is
 * probed to detect its auth type (AC: detect on valid URL, 10 s timeout
 * server-side). A failed probe is reported once the user leaves the field, so
 * a pause mid-typing never flags an unfinished URL as unreachable. Editing the
 * URL clears its errors and drops a probe still in flight.
 */
export function useMcpUrlCheck({
  url,
  setUrl,
  active,
  probeEnabled = true,
  onProbed,
}: UseMcpUrlCheckOptions) {
  const t = useTranslations("Mcp.addModal");
  const tMcp = useTranslations("Mcp");

  const [probe, setProbe] = useState<MCPProbeResult | null>(null);
  // URL the current `probe` belongs to.
  const [probedUrl, setProbedUrl] = useState("");
  const [probing, setProbing] = useState(false);
  // The format error, or one a save raised for this URL.
  const [urlError, setUrlError] = useState<string | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);
  const [urlFocused, setUrlFocused] = useState(false);
  const onProbedRef = useRef(onProbed);
  onProbedRef.current = onProbed;

  const mapProbeError = useCallback(
    (err: unknown): string => {
      if (err instanceof MCPProbeError) {
        const status = err.upstreamStatus ?? "";
        switch (err.code) {
          case "MCP_PROBE_HTTP_FORBIDDEN":
            return tMcp("add.errors.probeForbidden", { status });
          case "MCP_PROBE_NOT_MCP":
            return tMcp("add.errors.probeNotMcp", { status });
          case "MCP_PROBE_TIMEOUT":
            return tMcp("add.errors.probeTimeout");
          case "MCP_PROBE_UNREACHABLE":
            return tMcp("add.errors.probeUnreachable");
          default:
            return tMcp("add.errors.probeFailed");
        }
      }
      return err instanceof Error && err.message
        ? err.message
        : tMcp("add.errors.probeFailed");
    },
    [tMcp],
  );

  const trimmedUrl = url.trim();

  useEffect(() => {
    if (!active || !trimmedUrl || trimmedUrl === probedUrl) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!isValidHttpUrl(trimmedUrl)) {
        const typed = trimmedUrl.toLowerCase();
        if (!URL_SCHEMES.some((scheme) => scheme.startsWith(typed))) {
          setUrlError(t("errors.urlInvalid"));
        }
        return;
      }
      if (!probeEnabled) return;
      setProbing(true);
      setProbeError(null);
      probeMCP(trimmedUrl)
        .then((result) => {
          if (cancelled) return;
          setProbing(false);
          setProbe(result);
          setProbedUrl(trimmedUrl);
          onProbedRef.current?.(trimmedUrl);
        })
        .catch((err) => {
          if (cancelled) return;
          setProbing(false);
          setProbeError(mapProbeError(err));
        });
    }, CHECK_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setProbing(false);
    };
  }, [active, probeEnabled, trimmedUrl, probedUrl, mapProbeError, t]);

  const handleUrlChange = useCallback(
    (nextUrl: string) => {
      setUrl(nextUrl);
      // Errors belong to the value they were raised for.
      setUrlError(null);
      setProbeError(null);
    },
    [setUrl],
  );

  const reset = useCallback(() => {
    setProbe(null);
    setProbedUrl("");
    setProbing(false);
    setUrlError(null);
    setProbeError(null);
  }, []);

  const shownProbeError = probeEnabled ? probeError : null;

  return {
    /** Probe result of the current URL, or null until it probed successfully. */
    probe: probedUrl === trimmedUrl ? probe : null,
    probing,
    /** Error to show under the field. */
    urlError: urlError ?? (urlFocused ? null : shownProbeError),
    /** The current URL failed its check; a save may retry it. */
    checkFailed: Boolean(urlError || shownProbeError),
    setUrlError,
    mapProbeError,
    handleUrlChange,
    onUrlFocusChange: setUrlFocused,
    reset,
  };
}
